import API from '@aws-amplify/api'
import { Toolbar, makeStyles } from '@material-ui/core'
import MUIDataTable from 'mui-datatables'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { titleCase } from 'title-case'

import { useActionRunner, useNotification } from 'src/modules/notification'
import { Button, Container, FormikDialog, Typography } from 'src/modules/ui'
import { formatDateSearch, formatPrice, generateOrderNumber } from 'src/utils'

import { useOrders } from '../hooks'

const useStyles = makeStyles(theme => ({
  toolbar: {
    marginBottom: theme.spacing(1),
  },
  toolbarButton: {
    marginLeft: theme.spacing(1),
  },
}))

export const OrderList = ({ onRefresh, orders, status, title }) => {
  const classes = useStyles()
  const history = useHistory()
  const { markOrderDispatched, cancelOrder } = useOrders()

  const data = orders.map(order => {
    const { createdAt, buyer, price, statusChangedAt } = order
    return {
      ...order,
      id: generateOrderNumber(order),
      serverId: order.id,
      createdAt: formatDateSearch(createdAt),
      statusChangedAt: statusChangedAt && formatDateSearch(statusChangedAt),
      buyer: buyer.name,
      price: formatPrice(price),
    }
  })

  const columns = [
    {
      name: 'id',
      label: 'Order Number',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'buyer',
      label: 'Customer',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'price',
      label: 'Total',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'createdAt',
      label: 'Order Date',
      options: {
        filter: false,
        sort: true,
        sortDirection: 'asc',
      },
    },
    {
      name: 'scheduleLater',
      label: 'Requested Date',
      options: {
        filter: false,
        sort: true,
      },
    },
  ]

  if (['cancelled', 'dispatched'].includes(status)) {
    const label = {
      cancelled: 'Date Cancelled',
      dispatched: 'Date Dispatched',
    }
    columns.push({
      name: 'statusChangedAt',
      label: label[status],
      options: {
        filter: false,
        sort: true,
      },
    })
  }

  const options = {
    download: false,
    elevation: 0,
    onRowClick: (row, { dataIndex }) => {
      history.push(`/orders/${orders[dataIndex].id}`)
    },
    print: false,
    responsive: 'simple',
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    selectableRows: 'multiple',
    viewColumns: false,
  }

  return (
    <Container muiTable>
      <MUIDataTable
        columns={columns}
        components={{
          TableToolbarSelect: ({ selectedRows, ...props }) => {
            const { showError, showSuccess } = useNotification()
            const [exporting, setExporting] = useState(false)

            const numOrders = selectedRows.data.length
            const orderIds = selectedRows.data.map(
              ({ dataIndex }) => data[dataIndex].serverId
            )

            const handleMarkDispatched = async () => {
              const result = await Promise.all(
                orderIds.map(id => markOrderDispatched(id))
              )
              const errors = result.filter(a => a.error)
              onRefresh()
              if (errors.length) {
                return errors[0]
              } else {
                return {}
              }
            }

            const handleCancel = async () => {
              const result = await Promise.all(
                orderIds.map(id => cancelOrder(id))
              )
              const errors = result.filter(a => a.error)
              onRefresh()
              if (errors.length) {
                return errors[0]
              } else {
                return {}
              }
            }

            const handleExport = async () => {
              setExporting(true)

              try {
                const blob = await API.get(
                  'default',
                  `/supplier/orders/export/`,
                  {
                    queryStringParameters: { ids: orderIds },
                    responseType: 'blob',
                  }
                )

                const a = document.createElement('a')
                document.body.appendChild(a)
                a.style = 'display: none'

                const csvUrl = URL.createObjectURL(blob)
                a.href = csvUrl
                a.download = 'orders.csv'
                a.click()
                URL.revokeObjectURL(a.href)
                a.remove()

                showSuccess('Orders exported')
              } catch (err) {
                showError(err.message)
              } finally {
                setExporting(false)
              }
            }

            const markAsDispatched = (
              <FormikDialog
                key="markAsDispatched"
                onSubmit={handleMarkDispatched}
                submitText="Mark As Dispatched"
                successNotification={{
                  image: 'thyme',
                  message: `${pluralize(
                    'order',
                    numOrders,
                    true
                  )} marked as dispatched.\n\nLooking forward to the ride! Better get going... we need to arrive in thyme!`,
                }}
                trigger={props => (
                  <Button
                    className={classes.toolbarButton}
                    {...props}
                    color="lightGreen"
                  >
                    Mark As Dispatched
                  </Button>
                )}
              >
                <Typography>
                  Mark {pluralize('Order', numOrders, true)} As Dispatched?
                </Typography>
              </FormikDialog>
            )

            const cancel = (
              <FormikDialog
                key="cancel"
                onSubmit={handleCancel}
                submitText={`Cancel ${pluralize('Order', numOrders)}`}
                successNotification={`${pluralize(
                  'order',
                  numOrders,
                  true
                )} cancelled`}
                trigger={props => (
                  <Button
                    className={classes.toolbarButton}
                    {...props}
                    color="lightGreen"
                  >
                    Cancel {pluralize('Order', numOrders)}
                  </Button>
                )}
              >
                <Typography>
                  Cancel {pluralize('Order', numOrders, true)}?
                </Typography>
              </FormikDialog>
            )

            const exportCsv = (
              <Button
                key="exportCsv"
                className={classes.toolbarButton}
                color="lightGreen"
                onClick={handleExport}
                isLoading={exporting}
              >
                Export As Csv
              </Button>
            )

            const actions =
              status === 'submitted'
                ? [markAsDispatched, cancel, exportCsv]
                : [exportCsv]

            return (
              <Toolbar className={classes.toolbar}>
                <Typography>
                  {pluralize('order', numOrders, true)} selected
                </Typography>
                {actions}
              </Toolbar>
            )
          },
        }}
        data={data}
        options={options}
        title={title}
      />
    </Container>
  )
}

OrderList.propsTypes = {
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      buyer: PropTypes.object,
      createdAt: PropTypes.string,
      price: PropTypes.string,
      status: PropTypes.string,
    })
  ),
  status: PropTypes.oneOf(['dispatched', 'cancelled', 'submitted']),
  title: PropTypes.string,
}

const OrderListContainer = ({ section, title }) => {
  const { orders, fetchOrders } = useOrders()
  const [doFetchOrders, isFetching] = useActionRunner(fetchOrders, {
    initialFetchingState: true,
  })

  useEffect(() => {
    doFetchOrders(section)
  }, [])

  if (isFetching) {
    return null
  }

  return (
    <OrderList
      onRefresh={() => doFetchOrders(section)}
      orders={orders}
      status={section === 'new' ? 'submitted' : section}
      title={titleCase(section)}
    />
  )
}

OrderListContainer.propsTypes = {
  section: PropTypes.oneOf(['new', 'cancelled', 'submitted']),
}

export default OrderListContainer
